import { Action, Selector, State, type StateContext } from "@ngxs/store";
import { EMPTY } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { inject, Service } from "@angular/core";

import {
  type INotificationInbox,
  type INotificationItem,
} from "../interfaces/notification.model";
import { NotificationInboxService } from "../services/notification-inbox.service";

import {
  AddIncomingPush,
  ClearNotifications,
  LoadInbox,
  MarkAllAsRead,
  MarkAsRead,
} from "./notification.actions";

export interface NotificationStateModel {
  items: INotificationItem[];
  unreadCount: number;
  pagination: INotificationInbox["pagination"] | null;
  loading: boolean;
}

@State<NotificationStateModel>({
  name: "Notifications",
  defaults: { items: [], unreadCount: 0, pagination: null, loading: false },
})
@Service({ autoProvided: false })
export class NotificationState {
  private readonly inbox = inject(NotificationInboxService);

  @Selector()
  static items(state: NotificationStateModel) {
    return state.items;
  }

  @Selector()
  static unreadCount(state: NotificationStateModel) {
    return state.unreadCount;
  }

  @Action(LoadInbox)
  loadInbox(ctx: StateContext<NotificationStateModel>, action: LoadInbox) {
    ctx.patchState({ loading: true });
    return this.inbox.getInbox(action.tenant, action.pageIndex, 20, false).pipe(
      tap(res => {
        ctx.patchState({
          items: res.items,
          pagination: res.pagination,
          unreadCount: res.pagination.totalCount,
          loading: false,
        });
      }),
      catchError(() => {
        ctx.patchState({ loading: false });
        return EMPTY;
      })
    );
  }

  @Action(MarkAsRead)
  markAsRead(ctx: StateContext<NotificationStateModel>, action: MarkAsRead) {
    const removeFromList = () => {
      const state = ctx.getState();
      const items = state.items.filter(i => i.deliveryId !== action.deliveryId);
      const removed = state.items.length - items.length;
      ctx.patchState({
        items,
        unreadCount: Math.max(0, state.unreadCount - removed),
      });
    };
    return this.inbox.markAsRead(action.tenant, action.deliveryId).pipe(
      tap(() => removeFromList()),
      catchError(() => {
        removeFromList();
        return EMPTY;
      })
    );
  }

  @Action(MarkAllAsRead)
  markAllAsRead(
    ctx: StateContext<NotificationStateModel>,
    action: MarkAllAsRead
  ) {
    const unread = ctx.getState().items.filter(i => i.readAt === null);
    unread.forEach(i =>
      ctx.dispatch(new MarkAsRead(action.tenant, i.deliveryId))
    );
  }

  @Action(AddIncomingPush)
  addIncomingPush(
    ctx: StateContext<NotificationStateModel>,
    action: AddIncomingPush
  ) {
    return ctx.dispatch(new LoadInbox(action.tenant));
  }

  @Action(ClearNotifications)
  clear(ctx: StateContext<NotificationStateModel>) {
    ctx.setState({
      items: [],
      unreadCount: 0,
      pagination: null,
      loading: false,
    });
  }
}
